import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@spotted-zebra-uk/ui-components';

type SupportEmailDisclaimerProps = {
  /** Locale prefix of the step, e.g. `accessibilityAdjustments.needsList`. */
  i18nPrefix: string;
  /**
   * The company's "Display live chat to users" setting. When off, the copy
   * points candidates to email only.
   */
  isLiveChatEnabled: boolean;
  /** Test id prefix of the step, e.g. `accessibility-needs-list`. */
  testIdPrefix: string;
  className?: string;
};

/**
 * The "please reach out via … email" sentence under an adjustments step. The
 * word "email" is a mailto link to the company's effective support address,
 * so the address itself never appears in the copy (CR-473).
 */
const SupportEmailDisclaimer = ({
  i18nPrefix,
  isLiveChatEnabled,
  testIdPrefix,
  className,
}: SupportEmailDisclaimerProps) => {
  const { t } = useTranslation();
  const { supportEmail, supportEmailHref } = useSupportEmail();

  return (
    <p className={className} data-testid={`${testIdPrefix}__disclaimer`}>
      <Trans
        i18nKey={`${i18nPrefix}.${isLiveChatEnabled ? 'disclaimer' : 'disclaimerEmailOnly'}`}
        t={t}
      >
        {/* Every locale wraps its word for "email" in <1>…</1>: this link.
            The text around it comes from the locale, so the string children
            only fix the link at index 1. */}
        {''}
        <Link
          to={supportEmailHref}
          target="_blank"
          aria-label={t('common.sendEmailTo', { email: supportEmail })}
          data-testid={`${testIdPrefix}__support-email-link`}
        >
          email
        </Link>
        {''}
      </Trans>
    </p>
  );
};

export default SupportEmailDisclaimer;
